import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "../../css/models-common.css";
import "../../css/model.css";
import { CLASSIFICATION_COLORS } from "../../constants/models";
import { useTranslation } from "react-i18next";
import { translateSectorLabel } from "../../utils/sectorLabel";

// Both the Classification and Sources tabs share the same blue → teal → cyan palette
const SOURCE_COLORS = CLASSIFICATION_COLORS;

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.04) return null;

  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 16;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="rgba(255, 255, 255, 0.85)"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize="9"
      fontWeight="600"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const ClassificationPieChart = ({ classificationData, sourcesData, sourcesLoading }) => {
  const { i18n } = useTranslation();
  if (!classificationData || !classificationData.materials) return null;

  const { materials } = classificationData;

  const sourceCountries = sourcesData?.countries ?? [];

  return (
    <div className="news_comments mt-4">
      {/* Tabs */}
      <ul className="news-tabs" role="tablist">
        <li className="news-tab-item" role="presentation">
          <button
            className="news-tab-btn active"
            id="classification-tab"
            data-bs-toggle="pill"
            data-bs-target="#classification"
            type="button"
            role="tab"
            aria-controls="classification"
            aria-selected="true"
          >
            CLASSIFICATION
          </button>
        </li>
        <li className="news-tab-item" role="presentation">
          <button
            className="news-tab-btn"
            id="sources-tab"
            data-bs-toggle="pill"
            data-bs-target="#sources"
            type="button"
            role="tab"
            aria-controls="sources"
            aria-selected="false"
          >
            SOURCES
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      <div className="news-tab-content tab-content mt-4" id="classificationTabContent">
        {/* CLASSIFICATION Tab */}
        <div
          className="tab-pane fade show active"
          id="classification"
          role="tabpanel"
          aria-labelledby="classification-tab"
        >
          <div className="classification-chart__content">
            <div className="classification-chart__chart-wrapper">
              <div className="classification-chart__chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={materials}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={58}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                      labelLine={false}
                      label={renderCustomizedLabel}
                    >
                      {materials.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="classification-chart__center-label">
                  Materials
                </div>
              </div>
            </div>

            <div className="classification-chart__legend">
              {materials.map((material, index) => (
                <div key={index} className="classification-chart__legend-item">
                  <div className="classification-chart__legend-label">
                    <div
                      className="classification-chart__legend-color"
                      style={{ backgroundColor: material.color }}
                    />
                    <span className="classification-chart__legend-name">
                      {translateSectorLabel(material.name, i18n.resolvedLanguage || i18n.language)}
                    </span>
                  </div>
                  <span className="classification-chart__legend-value">
                    {material.value}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SOURCES Tab */}
        <div
          className="tab-pane fade"
          id="sources"
          role="tabpanel"
          aria-labelledby="sources-tab"
        >
          {sourcesLoading ? (
            <div className="u-empty-state">
              <p>Loading sources...</p>
            </div>
          ) : sourceCountries.length > 0 ? (
            <div className="classification-chart__content">
              <div className="classification-chart__chart-wrapper">
                <div className="classification-chart__chart-container">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={sourceCountries.map((c, i) => ({
                          name: c.country,
                          value: Math.round(c.percent),
                          color: SOURCE_COLORS[i % SOURCE_COLORS.length],
                        }))}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={58}
                        paddingAngle={4}
                        dataKey="value"
                        stroke="none"
                        labelLine={false}
                        label={renderCustomizedLabel}
                      >
                        {sourceCountries.map((_, i) => (
                          <Cell key={`source-cell-${i}`} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="classification-chart__center-label">
                    Countries
                  </div>
                </div>
              </div>

              <div className="classification-chart__legend classification-chart__legend--grid">
                {sourceCountries.map((country, index) => (
                  <div key={index} className="classification-chart__legend-item">
                    <div className="classification-chart__legend-label">
                      <div
                        className="classification-chart__legend-color"
                        style={{ backgroundColor: SOURCE_COLORS[index % SOURCE_COLORS.length] }}
                      />
                      <span className="classification-chart__legend-name">
                        {country.country}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="u-empty-state">
              <p>No source data available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassificationPieChart;
